import { useState, useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Hook to block navigation when a form has unsaved changes.
 *
 * Works with BrowserRouter by intercepting:
 *  - browser refresh/close (beforeunload)
 *  - in-app link clicks (capture-phase click listener)
 *  - the browser back/forward buttons (history sentinel + popstate)
 *
 * Note: the app uses the component <BrowserRouter>, not a data router, so
 * React Router's useBlocker is unavailable; we guard via the History API.
 *
 * @returns {Object} { isDirty, setIsDirty, resetDirty, isModalOpen, handleStay, handleDiscard }
 *
 * @example
 * const { isDirty, setIsDirty, resetDirty } = useFormNavigationGuard();
 *
 * // Mark form as dirty when user makes changes
 * <input onChange={() => setIsDirty(true)} />
 *
 * // Clear dirty flag after successful save
 * const handleSave = async () => {
 *   await save();
 *   resetDirty();
 * };
 */
const BACK_TARGET = '__back__';

export const useFormNavigationGuard = () => {
  const navigate = useNavigate();
  const [isDirty, setIsDirty] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  // Pending in-app destination (path) the user tried to reach, or BACK_TARGET
  // when the navigation attempt was a browser back/forward press.
  const pendingTarget = useRef<string | null>(null);
  // Lets a programmatic history.back() (triggered on discard) pass through.
  const allowPop = useRef(false);

  const resetDirty = useCallback(() => {
    setIsDirty(false);
  }, []);

  const handleStay = useCallback(() => {
    setIsModalOpen(false);
    pendingTarget.current = null;
  }, []);

  const handleDiscard = useCallback(() => {
    setIsModalOpen(false);
    setIsDirty(false);

    const target = pendingTarget.current;
    pendingTarget.current = null;

    if (target === BACK_TARGET) {
      allowPop.current = true;
      window.history.back();
    } else if (target) {
      navigate(target);
    }
  }, [navigate]);

  // Listen for browser close/refresh
  useEffect(() => {
    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      if (isDirty) {
        e.preventDefault();
        e.returnValue = ''; // Chrome requires returnValue to be set
      }
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
    };
  }, [isDirty]);

  // Intercept in-app link clicks before React Router navigates
  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (!isDirty) return;
      if (event.defaultPrevented) return;
      // Let modified clicks (new tab/window) and non-primary buttons through
      if (event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) {
        return;
      }

      const target = event.target as Element | null;
      // Don't block clicks inside the unsaved-changes modal itself
      if (target && target.closest('[data-unsaved-changes-modal]')) {
        return;
      }

      let anchorTarget = event.target as HTMLElement | null;
      while (anchorTarget && anchorTarget.tagName !== 'A') {
        anchorTarget = anchorTarget.parentElement;
      }
      if (!anchorTarget) return;

      const anchor = anchorTarget as HTMLAnchorElement;
      if (anchor.target && anchor.target !== '_self') return; // opens elsewhere

      const url = new URL(anchor.href, window.location.origin);
      if (url.origin !== window.location.origin) return; // external link
      // Same page (no real navigation) — let it through
      if (url.pathname === window.location.pathname && url.search === window.location.search) {
        return;
      }

      pendingTarget.current = url.pathname + url.search + url.hash;
      setIsModalOpen(true);
      event.preventDefault();
      event.stopPropagation();
    };

    window.addEventListener('click', handleClick, true);
    return () => {
      window.removeEventListener('click', handleClick, true);
    };
  }, [isDirty]);

  // Intercept browser back/forward buttons
  useEffect(() => {
    if (!isDirty) return;

    // Push a sentinel entry so the first Back lands here and fires popstate
    // instead of leaving the page outright.
    window.history.pushState(null, '', window.location.href);

    const handlePop = () => {
      if (allowPop.current) {
        allowPop.current = false;
        return;
      }
      // Re-trap and prompt; stay on the current page until the user decides.
      window.history.pushState(null, '', window.location.href);
      pendingTarget.current = BACK_TARGET;
      setIsModalOpen(true);
    };

    window.addEventListener('popstate', handlePop);
    return () => {
      window.removeEventListener('popstate', handlePop);
    };
  }, [isDirty]);

  return {
    isDirty,
    setIsDirty,
    resetDirty,
    isModalOpen,
    handleStay,
    handleDiscard,
  };
};
